import { useState, useEffect } from 'react';
import { X, FolderOpen } from 'lucide-react';
import { useTranslation } from '../lib/LanguageContext';
import { getUserSubscription } from '../lib/subscription';

interface Project {
  id: string;
  name: string;
  user_id: string;
}

interface Todo {
  id: string;
  text: string;
  status: 'backlog' | 'in_progress' | 'review' | 'testing' | 'done';
  user_id: string;
  project_id: string;
  notes: string | null;
  priority: boolean;
  color: 'yellow' | 'green' | 'blue' | null;
  completed_at: string | null;
  deadline?: string | null;
}

interface MoveToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectId: string) => void;
  todo: Todo | null;
  projects: Project[];
  currentProjectId: string;
}

export function MoveToProjectModal({
  isOpen,
  onClose,
  onSave,
  todo,
  projects,
  currentProjectId
}: MoveToProjectModalProps) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isPremium, setIsPremium] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && projects.length > 0) {
      // Set first non-current project as default
      const firstOtherProject = projects.find(p => p.id !== currentProjectId);
      setSelectedProjectId(firstOtherProject?.id || '');
    }
  }, [isOpen, projects, currentProjectId]);

  useEffect(() => {
    if (isOpen) {
      checkPremiumStatus();
    }
  }, [isOpen]);

  const checkPremiumStatus = async () => {
    try {
      const subscriptionData = await getUserSubscription();
      setIsPremium(subscriptionData?.subscriptionTier === 'premium' || false);
    } catch (error) {
      setIsPremium(false);
    }
  };

  const handleSave = () => {
    if (selectedProjectId && selectedProjectId !== currentProjectId) {
      onSave(selectedProjectId);
      onClose();
    }
  };

  if (!isOpen || !todo) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FolderOpen className="h-5 w-5 mr-2" />
            {t('moveToProjectTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!isPremium ? (
          <div className="px-6 py-4">
            <div className="bg-amber-50 p-4 rounded-md text-amber-800 mb-4">
              <p className="font-medium">{t('premiumFeature')}</p>
              <p className="text-sm mt-1">
                {t('moveToProjectPremiumOnly')}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('close')}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4">
            <div className="mb-4">
              <div className="font-medium text-gray-800 mb-2">{t('taskLabel')}</div>
              <div className="bg-indigo-50 p-3 rounded-md text-gray-700">
                {todo.text}
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectDestinationProject')}
              </label>
              <select
                id="project"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              >
                <option value="" disabled>Select a project...</option>
                {projects.map((project) => (
                  <option
                    key={project.id}
                    value={project.id}
                    disabled={project.id === currentProjectId}
                  >
                    {project.name}
                    {project.id === currentProjectId ? ` (${t('currentProject')})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedProjectId || selectedProjectId === currentProjectId}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('moveButton')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
